import React from "react";

import { Grid } from "@mui/material";

import CheckboxV2 from "@src/sharedComponents/Checkbox/CheckboxV2";
import FormFieldLabel from "@src/sharedComponents/Forms/Private/FormFieldLabel";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import SingleFileDropUpload from "@src/sharedComponents/SingleFileDropUpload/SingleFileDropUpload";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./CreateTransferSummary.module.css";

const SummaryRow = ({
  label,
  value,
  isHeadline = false,
}: {
  label: string;
  value: string;
  isHeadline?: boolean;
}) => (
  <GridRow className={styles.tableRow}>
    <Grid item xs>
      <Text variant="body">{label}</Text>
    </Grid>
    <Grid item xs className={styles.valuesList}>
      <Text variant={isHeadline ? "title1" : "body"}>{value}</Text>
    </Grid>
  </GridRow>
);

interface CreateTransferSummaryProps {
  onEditClick: () => void;
  amount: string | null;
  transferType: string;
  withdrawFrom?: string;
  depositTo?: string;
  contributionYear?: string;
  iraType?: string;
  accountType?: string;
  institution?: string;
  accountNumber?: string;
  accountHolderName?: string;
  accountDestination?: string;
  statementFile?: File | null;
  requestSigned?: boolean;
}

const CreateTransferSummary: React.FC<CreateTransferSummaryProps> = ({
  onEditClick,
  amount,
  transferType,
  withdrawFrom,
  depositTo,
  contributionYear,
  accountType,
  iraType,
  institution,
  accountNumber,
  accountHolderName,
  accountDestination,
  statementFile,
  requestSigned,
}) => {
  // NOTE: currently no transfer summary will show "All" for an amount, leaving it as it might come handy in the future.
  const transferAmount = amount === null ? "All" : amount;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text variant="title2">Transfer Summary</Text>

        <Text
          variant="bodySmall"
          className={styles.editText}
          onClick={onEditClick}
        >
          Edit
        </Text>
      </div>

      <div className={styles.tableContainer}>
        <Grid container wrap="wrap" alignItems="center">
          {["Deposit", "Withdrawal"].includes(transferType) && (
            <SummaryRow label="Amount" value={transferAmount} isHeadline />
          )}

          <SummaryRow label="Transfer Type" value={transferType} />

          {withdrawFrom && (
            <SummaryRow label="Withdrawal From" value={withdrawFrom} />
          )}

          {depositTo && <SummaryRow label="Deposit To" value={depositTo} />}

          {contributionYear && (
            <SummaryRow label="Contribution Year" value={contributionYear} />
          )}

          {accountType && (
            <SummaryRow label="Account Type" value={accountType} />
          )}

          {iraType && <SummaryRow label="IRA Type" value={iraType} />}

          {institution && (
            <SummaryRow label="Institution" value={institution} />
          )}

          {accountNumber && (
            <SummaryRow label="Account Number" value={accountNumber} />
          )}

          {accountHolderName && (
            <SummaryRow label="Account Name Holder" value={accountHolderName} />
          )}

          {accountDestination && (
            <SummaryRow
              label="Account Destination"
              value={accountDestination}
            />
          )}
        </Grid>
      </div>

      {statementFile && (
        <>
          <SingleFileDropUpload
            className={styles.fileFrame}
            file={statementFile}
            onChange={() => void null}
          />
          <div className={styles.checkboxFrame}>
            <CheckboxV2 checked={Boolean(requestSigned)} readOnly />
            <FormFieldLabel
              label={
                <div className={styles.checkBoxLabel}>
                  Click here to sign this transfer request.
                </div>
              }
            />
          </div>
        </>
      )}
    </div>
  );
};

export default CreateTransferSummary;
